import * as THREE from 'three';
import { isPointClipped } from './geometry';
import { clamp, fitCircle2D, jacobiEigen3, type Mat3 } from './math';
import {
  isMeasurePanelOpen,
  primaryValueIdForMode,
  renderMeasureList,
  renderMeasurePanel,
  setMeasureListHandlers,
  setMeasureModeChangeHandler,
  setMeasurePanelOpen,
  setMeasurePanelPrimary,
  setMeasureStatus,
  setMeasureValueSelectHandler
} from './measure-panel';
import { resolveMeasurement, segmentForMode } from './measure-resolve';
import { clearTopologyCache, snapTopology } from './measure-topology';
import type { AxisBasis, DistanceMode, FaceAnalysis, Measurement, MeasureSegment, OcctBrepFace, Pick, RawPick } from './types';
import { dom, formatAngleDeg, formatDistance, log, onLengthUnitChange } from './ui';
import {
  camera,
  disposeMesh,
  getModelGroup,
  getModelRadius,
  onFrame,
  raycaster,
  renderer,
  requestRender,
  scene,
  theme
} from './viewer';

export { clearTopologyCache };

let measureMode = false;
let pendingPoint: Pick | null = null;
let pendingMarker: THREE.Object3D | null = null;
let pendingHighlight: THREE.Object3D | null = null;
let pendingCircle: THREE.Object3D | null = null;
let pendingAxis: THREE.Object3D | null = null;
let pendingLabel: HTMLElement | null = null;
/** All measurements, oldest first. */
const measurements: Measurement[] = [];
/** The measurement whose details the panel shows (usually the newest). */
let currentMeasurement: Measurement | null = null;
let nextMeasureId = 1;

/** Called when the measure tool takes over so other pick modes can stand down. */
let onMeasureActivated: () => void = () => {};

export function setMeasureActivatedHook(fn: () => void): void {
  onMeasureActivated = fn;
}

export function isMeasureMode(): boolean {
  return measureMode;
}

export function hasMeasurement(): boolean {
  return measurements.length > 0 || pendingPoint !== null;
}

function markerRadius(): number {
  return Math.max(getModelRadius() * 0.008, 0.05);
}

function addMarker(position: THREE.Vector3, color: number): THREE.Mesh {
  const geo = new THREE.SphereGeometry(markerRadius(), 16, 16);
  const mat = new THREE.MeshBasicMaterial({ color, depthTest: false, toneMapped: false });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.copy(position);
  mesh.renderOrder = 999;
  scene.add(mesh);
  requestRender();
  return mesh;
}

// ---------------------------------------------------------------------------
// Picking
// ---------------------------------------------------------------------------

export function pickFaceOnModel(clientX: number, clientY: number): RawPick | null {
  const modelGroup = getModelGroup();
  if (!modelGroup) return null;
  const rect = dom.canvas.getBoundingClientRect();
  const ndc = new THREE.Vector2(
    ((clientX - rect.left) / rect.width) * 2 - 1,
    -((clientY - rect.top) / rect.height) * 2 + 1
  );
  raycaster.setFromCamera(ndc, camera);
  const hits = raycaster.intersectObject(modelGroup, true);
  // Not hits[0]: a section hides geometry from the eye, not from the ray, so
  // skip whatever it cut away - otherwise a click inside a sectioned room
  // lands on the roof above it.
  const hit = hits.find(h => h.face && !isPointClipped(h.point, renderer.clippingPlanes));
  if (!hit || !hit.face) return null;
  const mesh = hit.object as THREE.Mesh;
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
  const worldNormal = hit.face.normal.clone().applyMatrix3(normalMatrix).normalize();
  const brepFace = findBrepFace(mesh, hit.faceIndex ?? 0);
  return { point: hit.point.clone(), normal: worldNormal, mesh, triangleIndex: hit.faceIndex ?? 0, brepFace };
}

function findBrepFace(mesh: THREE.Mesh, triangleIndex: number): OcctBrepFace | null {
  const faces = mesh.userData.brepFaces as OcctBrepFace[] | null | undefined;
  if (!faces || faces.length === 0) return null;
  // Ranges are sorted and non-overlapping: binary search (meshes can have thousands of faces).
  let lo = 0, hi = faces.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const f = faces[mid];
    if (triangleIndex < f.first) hi = mid - 1;
    else if (triangleIndex > f.last) lo = mid + 1;
    else return f;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Analytic fitting -- occt-import-js only exports the triangulated mesh, not
// the underlying analytic surfaces, so cylindrical/conical faces (holes,
// bosses, fillets) are detected and fitted from the tessellation: all normals
// on a true cylinder/cone lie in a plane perpendicular to its axis, so PCA on
// the face's triangle normals finds that axis, and a least-squares circle fit
// in the plane perpendicular to it gives the radius and center. This is an
// approximation of the tessellation, not the exact CAD value, but is normally
// accurate to a small fraction of a percent for reasonably fine triangulations.
// ---------------------------------------------------------------------------

function analyzeFaceGeometry(mesh: THREE.Mesh, range: { first: number; last: number }): FaceAnalysis {
  const geometry = mesh.geometry;
  const posAttr = geometry.getAttribute('position');
  const index = geometry.index;
  if (!posAttr || !index) return { kind: 'freeform' };
  const origIndex = index.array;

  const normals: THREE.Vector3[] = [];
  const vertexSet = new Set<number>();
  for (let t = range.first; t <= range.last; t++) {
    const i0 = origIndex[t * 3], i1 = origIndex[t * 3 + 1], i2 = origIndex[t * 3 + 2];
    vertexSet.add(i0); vertexSet.add(i1); vertexSet.add(i2);
    const pA = new THREE.Vector3().fromBufferAttribute(posAttr as THREE.BufferAttribute, i0);
    const pB = new THREE.Vector3().fromBufferAttribute(posAttr as THREE.BufferAttribute, i1);
    const pC = new THREE.Vector3().fromBufferAttribute(posAttr as THREE.BufferAttribute, i2);
    const n = new THREE.Vector3().subVectors(pB, pA).cross(new THREE.Vector3().subVectors(pC, pA));
    if (n.lengthSq() > 1e-14) { n.normalize(); normals.push(n); }
  }
  if (normals.length === 0) return { kind: 'freeform' };

  const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
  const worldNormals = normals.map(n => n.clone().applyMatrix3(normalMatrix).normalize());

  const cov: Mat3 = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  worldNormals.forEach(n => {
    cov[0][0] += n.x * n.x; cov[0][1] += n.x * n.y; cov[0][2] += n.x * n.z;
    cov[1][1] += n.y * n.y; cov[1][2] += n.y * n.z;
    cov[2][2] += n.z * n.z;
  });
  cov[1][0] = cov[0][1]; cov[2][0] = cov[0][2]; cov[2][1] = cov[1][2];
  const nCount = worldNormals.length;
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) cov[i][j] /= nCount;

  const eig = jacobiEigen3(cov);
  const order = [0, 1, 2].sort((a, b) => eig.values[a] - eig.values[b]);
  const smallest = Math.max(eig.values[order[0]], 0);
  const middle = Math.max(eig.values[order[1]], 0);
  const largest = Math.max(eig.values[order[2]], 1e-12);

  const worldPoints = Array.from(vertexSet).map(i =>
    new THREE.Vector3().fromBufferAttribute(posAttr as THREE.BufferAttribute, i).applyMatrix4(mesh.matrixWorld)
  );

  // Normal-covariance shapes:
  //   Plane:    ~ (0, 0, 1) — normals clustered → one large eigenvalue
  //   Cylinder: ~ (0, a, a) — normals fan around an axis → one tiny eigenvalue
  // Note: identical normals give largest≈1, NOT ≈0 (old largest<1e-5 check was wrong).
  if (middle / largest < 0.08) {
    const largestVec = eig.vectors[order[2]];
    const n = new THREE.Vector3(largestVec[0], largestVec[1], largestVec[2]).normalize();
    return { kind: 'plane', normal: n };
  }

  // Point-planarity fallback (handles noisy normals on flat faces).
  const planar = fitPlaneToPoints(worldPoints);
  if (planar && planar.relativeResidual < 0.01) {
    return { kind: 'plane', normal: planar.normal };
  }

  if (smallest / largest > 0.02) return { kind: 'freeform' };   // no clear rotational axis

  const smallestVec = eig.vectors[order[0]];
  const axis = new THREE.Vector3(smallestVec[0], smallestVec[1], smallestVec[2]).normalize();
  let u = new THREE.Vector3(1, 0, 0);
  if (Math.abs(axis.dot(u)) > 0.9) u = new THREE.Vector3(0, 1, 0);
  u.sub(axis.clone().multiplyScalar(u.dot(axis))).normalize();
  const v = new THREE.Vector3().crossVectors(axis, u);

  const refPoint = worldPoints[0].clone();
  const local = worldPoints.map(p => {
    const rel = p.clone().sub(refPoint);
    return { x: rel.dot(u), y: rel.dot(v), z: rel.dot(axis) };
  });
  const fit = fitCircle2D(local);
  if (!fit || !(fit.radius > 1e-6)) return { kind: 'freeform' };

  let residual = 0, zSum = 0;
  local.forEach(p => { const d = Math.hypot(p.x - fit.cx, p.y - fit.cy) - fit.radius; residual += d * d; zSum += p.z; });
  residual = Math.sqrt(residual / local.length);
  if (residual / fit.radius > 0.03) return { kind: 'freeform' }; // fit doesn't hold up -> not a clean cylinder/cone

  const zCenter = zSum / local.length;
  const center = refPoint.clone().addScaledVector(u, fit.cx).addScaledVector(v, fit.cy).addScaledVector(axis, zCenter);
  return { kind: 'cylinder', point: center, normal: axis, radius: fit.radius, axisBasis: { u, v, axis } };
}

/** Least-squares plane through points; relativeResidual = rms / extent. */
function fitPlaneToPoints(points: THREE.Vector3[]): { normal: THREE.Vector3; relativeResidual: number } | null {
  if (points.length < 3) return null;
  const centroid = new THREE.Vector3();
  points.forEach(p => centroid.add(p));
  centroid.multiplyScalar(1 / points.length);

  const cov: Mat3 = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  let extent = 0;
  points.forEach(p => {
    const d = p.clone().sub(centroid);
    extent = Math.max(extent, d.length());
    cov[0][0] += d.x * d.x; cov[0][1] += d.x * d.y; cov[0][2] += d.x * d.z;
    cov[1][1] += d.y * d.y; cov[1][2] += d.y * d.z;
    cov[2][2] += d.z * d.z;
  });
  cov[1][0] = cov[0][1]; cov[2][0] = cov[0][2]; cov[2][1] = cov[1][2];
  if (extent < 1e-9) return null;

  const eig = jacobiEigen3(cov);
  const order = [0, 1, 2].sort((a, b) => eig.values[a] - eig.values[b]);
  const nVec = eig.vectors[order[0]];
  const normal = new THREE.Vector3(nVec[0], nVec[1], nVec[2]).normalize();

  let rms = 0;
  points.forEach(p => {
    const dist = p.clone().sub(centroid).dot(normal);
    rms += dist * dist;
  });
  rms = Math.sqrt(rms / points.length);
  return { normal, relativeResidual: rms / extent };
}

/**
 * Face pick, augmented: for a cylindrical/conical face the reported point
 * snaps to the fitted axis center (not wherever you happened to click on the
 * rim) and the normal becomes the axis direction -- so a hole can be measured
 * from its true center, e.g. hole-to-face position.
 */
export function enrichPick(pick: RawPick): Pick {
  const range = pick.brepFace || { first: pick.triangleIndex, last: pick.triangleIndex };
  const analysis = analyzeFaceGeometry(pick.mesh, range);
  if (analysis.kind === 'cylinder' && analysis.point && analysis.normal) {
    return {
      ...pick,
      kind: 'cylinder',
      point: analysis.point,
      normal: analysis.normal,
      radius: analysis.radius,
      axisBasis: analysis.axisBasis
    };
  }
  if (analysis.kind === 'plane' && analysis.normal) {
    // Keep the click point; replace the single-triangle normal with the face fit.
    const n = analysis.normal.clone().normalize();
    if (n.dot(pick.normal) < 0) n.negate();
    return { ...pick, kind: 'plane', normal: n };
  }
  return { ...pick, kind: analysis.kind };
}

/**
 * Face hit + screen-space topology snap (vertex > edge > face).
 * Used by the measure tool; section picking still uses enrichPick alone.
 */
export function pickEntity(clientX: number, clientY: number): Pick | null {
  const raw = pickFaceOnModel(clientX, clientY);
  if (!raw) return null;
  const rect = dom.canvas.getBoundingClientRect();
  const snap = snapTopology(raw.mesh, raw.brepFace, raw.triangleIndex, clientX, clientY, rect, renderer.clippingPlanes);
  if (snap?.kind === 'vertex') {
    return { ...raw, kind: 'vertex', point: snap.point };
  }
  if (snap?.kind === 'edge' && snap.edgeA && snap.edgeB) {
    return {
      ...raw,
      kind: 'edge',
      point: snap.point,
      edgeA: snap.edgeA,
      edgeB: snap.edgeB
    };
  }
  return enrichPick(raw);
}

// ---------------------------------------------------------------------------
// Highlights
// ---------------------------------------------------------------------------

function buildCircleOutline(center: THREE.Vector3, axisBasis: AxisBasis, radius: number, color: number): THREE.Line {
  const segments = 48;
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    pts.push(center.clone().addScaledVector(axisBasis.u, Math.cos(t) * radius).addScaledVector(axisBasis.v, Math.sin(t) * radius));
  }
  const geo = new THREE.BufferGeometry().setFromPoints(pts);
  const mat = new THREE.LineBasicMaterial({ color, depthTest: false, toneMapped: false });
  const line = new THREE.Line(geo, mat);
  line.renderOrder = 996;
  scene.add(line);
  requestRender();
  return line;
}

/**
 * Highlights the whole logical CAD face the click landed on (all its
 * triangles), not just the point -- same idea as picking a face in
 * Inventor/SolidWorks measure tools. Falls back to just the hit triangle if no
 * brep_faces range was found (e.g. a format without that data).
 */
function buildFaceHighlight(pick: RawPick, color: number, opacity?: number): THREE.Group {
  const geometry = pick.mesh.geometry;
  const origIndex = geometry.index!.array as ArrayLike<number> & { slice(a: number, b: number): ArrayLike<number> };
  const range = pick.brepFace || { first: pick.triangleIndex, last: pick.triangleIndex };
  const count = (range.last - range.first + 1) * 3;
  const subIndex = origIndex.slice(range.first * 3, range.first * 3 + count) as Uint32Array;

  const highlightGeo = new THREE.BufferGeometry();
  highlightGeo.setAttribute('position', geometry.getAttribute('position'));
  if (geometry.getAttribute('normal')) highlightGeo.setAttribute('normal', geometry.getAttribute('normal'));
  highlightGeo.setIndex(new THREE.BufferAttribute(subIndex, 1));

  const material = new THREE.MeshBasicMaterial({
    toneMapped: false,
    color, transparent: true, opacity: opacity != null ? opacity : 0.55, side: THREE.DoubleSide,
    depthTest: true, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4
  });
  const highlightMesh = new THREE.Mesh(highlightGeo, material);
  highlightMesh.renderOrder = 900;

  const edgesGeo = new THREE.EdgesGeometry(highlightGeo, 25);
  const edgesMat = new THREE.LineBasicMaterial({ color, depthTest: false, linewidth: 2, toneMapped: false });
  const edgesLine = new THREE.LineSegments(edgesGeo, edgesMat);
  edgesLine.renderOrder = 901;

  const group = new THREE.Group();
  group.add(highlightMesh, edgesLine);
  pick.mesh.parent?.add(group); // same local space as the picked mesh
  requestRender();
  return group;
}

function buildEdgeHighlight(a: THREE.Vector3, b: THREE.Vector3, color: number): THREE.Line {
  const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
  const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color, depthTest: false, toneMapped: false }));
  line.renderOrder = 902;
  scene.add(line);
  requestRender();
  return line;
}

function buildEntityHighlight(pick: Pick, color: number, opacity?: number): THREE.Object3D {
  if (pick.kind === 'vertex') return addMarker(pick.point, color);
  if (pick.kind === 'edge' && pick.edgeA && pick.edgeB) {
    return buildEdgeHighlight(pick.edgeA, pick.edgeB, color);
  }
  return buildFaceHighlight(pick, color, opacity);
}

// ---------------------------------------------------------------------------
// Measurement flow
// ---------------------------------------------------------------------------

export function handleMeasureClick(clientX: number, clientY: number): void {
  const pick = pickEntity(clientX, clientY);
  if (!pick) return;
  clearHoverHighlight();
  if (pick.kind === 'cylinder' && pick.radius != null) {
    log('Cylindrical face: ⌀ ' + formatDistance(pick.radius * 2) + ' (R ' + formatDistance(pick.radius) + ')', true);
  } else if (pick.kind === 'edge') {
    log('Edge selected', true);
  } else if (pick.kind === 'vertex') {
    log('Vertex selected', true);
  }

  if (!pendingPoint) {
    pendingPoint = pick;
    pendingMarker = pick.kind === 'vertex' ? null : addMarker(pick.point, theme().accent);
    pendingHighlight = buildEntityHighlight(pick, theme().accent);
    if (pick.kind === 'cylinder' && pick.axisBasis && pick.radius != null) {
      pendingCircle = buildCircleOutline(pick.point, pick.axisBasis, pick.radius, theme().accent);
      pendingAxis = buildAxisLine(pick.point, pick.axisBasis.axis, Math.max(pick.radius * 1.6, 0.5), theme().accent);
      scene.add(pendingAxis);
      pendingLabel = document.createElement('div');
      pendingLabel.className = 'measure-label';
      document.body.appendChild(pendingLabel);
      updatePendingLabel();
    }
    setMeasureStatus('Select second entity…');
  } else {
    const a = pendingPoint;
    const b = pick;
    pendingPoint = null;
    clearPending();
    createMeasurement(a, b);
  }
  requestRender();
}

function buildAxisLine(origin: THREE.Vector3, dir: THREE.Vector3, halfLen: number, color: number): THREE.Line {
  const d = dir.clone().normalize();
  const a = origin.clone().addScaledVector(d, -halfLen);
  const b = origin.clone().addScaledVector(d, halfLen);
  const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
  const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color, depthTest: false, toneMapped: false }));
  line.renderOrder = 997;
  return line;
}

function clearPending(): void {
  if (pendingMarker) { disposeMesh(pendingMarker); pendingMarker = null; }
  if (pendingHighlight) { disposeMesh(pendingHighlight); pendingHighlight = null; }
  if (pendingCircle) { disposeMesh(pendingCircle); pendingCircle = null; }
  if (pendingAxis) { disposeMesh(pendingAxis); pendingAxis = null; }
  if (pendingLabel) { pendingLabel.remove(); pendingLabel = null; }
}

function createMeasurement(a: Pick, b: Pick, mode?: DistanceMode, replace?: Measurement): void {
  const result = resolveMeasurement(a, b);
  const activeMode = mode && result.distanceModes.includes(mode) ? mode : result.defaultMode;
  const seg = segmentForMode(result, activeMode);

  const highlightA = buildEntityHighlight(a, theme().teal);
  const highlightB = buildEntityHighlight(b, theme().teal);
  const extraLines: THREE.Object3D[] = [];
  if (a.kind === 'cylinder' && a.axisBasis && a.radius != null) {
    extraLines.push(buildCircleOutline(a.point, a.axisBasis, a.radius, theme().teal));
  }
  if (b.kind === 'cylinder' && b.axisBasis && b.radius != null) {
    extraLines.push(buildCircleOutline(b.point, b.axisBasis, b.radius, theme().teal));
  }
  if (result.axisA) {
    const line = buildAxisLine(result.axisA.origin, result.axisA.dir, result.axisA.halfLen, theme().teal);
    scene.add(line);
    extraLines.push(line);
  }
  if (result.axisB) {
    const line = buildAxisLine(result.axisB.origin, result.axisB.dir, result.axisB.halfLen, theme().teal);
    scene.add(line);
    extraLines.push(line);
  }

  const distLine = buildDistanceLine(seg.a, seg.b, theme().teal);

  const labelEl = document.createElement('div');
  labelEl.className = 'measure-label';
  document.body.appendChild(labelEl);

  let angleLabelEl: HTMLElement | null = null;
  let angleLabelPos: THREE.Vector3 | null = null;
  // Arc uses face/axis normals as-is — meaningful for plane–plane and cylinder–cylinder.
  const arcOk = (a.kind === 'plane' && b.kind === 'plane')
    || (a.kind === 'cylinder' && b.kind === 'cylinder');
  if (result.showAngle && arcOk) {
    const angleArc = buildAngleArc(a, b);
    if (angleArc) {
      extraLines.push(...angleArc.objects);
      angleLabelPos = angleArc.labelPos;
      angleLabelEl = document.createElement('div');
      angleLabelEl.className = 'measure-label angle';
      angleLabelEl.textContent = formatAngleDeg(result.angleDeg);
      document.body.appendChild(angleLabelEl);
    }
  }

  const primaryId = primaryValueIdForMode(activeMode, result);
  const m: Measurement = {
    id: replace ? replace.id : nextMeasureId++,
    visible: true,
    picks: [a, b],
    result,
    mode: activeMode,
    endpointA: seg.a.clone(),
    endpointB: seg.b.clone(),
    displayValueId: primaryId,
    mid: seg.mid.clone(),
    primaryDist: seg.distance,
    angleDeg: result.angleDeg,
    isParallel: result.isParallel,
    angleLabelPos,
    distLine,
    deltaGuide: null,
    lines: [...extraLines],
    markers: [highlightA, highlightB],
    labelEl,
    angleLabelEl
  };
  const slot = replace ? measurements.indexOf(replace) : -1;
  if (slot >= 0) measurements[slot] = m;
  else measurements.push(m);
  currentMeasurement = m;

  renderMeasurePanel(result, activeMode, primaryId);
  setMeasureStatus(`Measurement #${m.id} done — pick two more entities for the next one. Click a value to copy.`);
  refreshMeasureList();
  buildDimension(m);
  updateMeasurementLabel(m);

  const primary = result.values.find(v => v.id === primaryId);
  log(
    'Measure (' + result.title + '): '
    + (primary ? primary.label + ' ' + (primary.unit === 'deg' ? formatAngleDeg(primary.value) : formatDistance(primary.value)) : formatDistance(seg.distance)),
    true
  );
  requestRender();
}

function buildDistanceLine(a: THREE.Vector3, b: THREE.Vector3, color: number): THREE.Line {
  const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
  const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color, depthTest: false, toneMapped: false }));
  line.renderOrder = 998;
  scene.add(line);
  return line;
}

/** Orthogonal stair A → (Bx,Ay,Az) → (Bx,By,Az) → B for CAD-style Δ breakdown. */
function orthogonalCorners(a: THREE.Vector3, b: THREE.Vector3): [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3] {
  const p0 = a.clone();
  const p1 = new THREE.Vector3(b.x, a.y, a.z);
  const p2 = new THREE.Vector3(b.x, b.y, a.z);
  const p3 = b.clone();
  return [p0, p1, p2, p3];
}

function segmentBetween(a: THREE.Vector3, b: THREE.Vector3): MeasureSegment {
  return {
    a: a.clone(),
    b: b.clone(),
    mid: a.clone().add(b).multiplyScalar(0.5),
    distance: a.distanceTo(b)
  };
}

function displaySegmentForValue(a: THREE.Vector3, b: THREE.Vector3, valueId: string): MeasureSegment | null {
  if (valueId === 'xyz') return segmentBetween(a, b);
  const [p0, p1, p2, p3] = orthogonalCorners(a, b);
  if (valueId === 'dx') return segmentBetween(p0, p1);
  if (valueId === 'dy') return segmentBetween(p1, p2);
  if (valueId === 'dz') return segmentBetween(p2, p3);
  return null;
}

function deltaLineColor(valueId: string): number {
  if (valueId === 'dx') return 0xe74c3c;
  if (valueId === 'dy') return 0x2ecc71;
  if (valueId === 'dz') return 0x3498db;
  return theme().teal;
}

function buildDeltaGuide(a: THREE.Vector3, b: THREE.Vector3): THREE.Line {
  const pts = orthogonalCorners(a, b);
  const geo = new THREE.BufferGeometry().setFromPoints(pts);
  const mat = new THREE.LineDashedMaterial({
    toneMapped: false,
    color: 0x889099,
    dashSize: Math.max(getModelRadius() * 0.01, 0.15),
    gapSize: Math.max(getModelRadius() * 0.006, 0.08),
    depthTest: false,
    transparent: true,
    opacity: 0.55
  });
  const line = new THREE.Line(geo, mat);
  line.computeLineDistances();
  line.renderOrder = 997;
  scene.add(line);
  return line;
}

function applyDisplayValue(valueId: string): void {
  const m = currentMeasurement;
  if (!m) return;
  if (!m.result.values.some(v => v.id === valueId)) return;

  // Δ / 3D use the pick anchors; perp/min/center/max restore the resolved segment.
  const pickA = m.picks[0].point;
  const pickB = m.picks[1].point;
  let displaySeg: MeasureSegment | null = null;
  let showGuide = false;
  if (valueId === 'dx' || valueId === 'dy' || valueId === 'dz') {
    displaySeg = displaySegmentForValue(pickA, pickB, valueId);
    showGuide = true;
  } else if (valueId === 'xyz') {
    displaySeg = displaySegmentForValue(pickA, pickB, 'xyz');
  } else if (valueId === 'perp') {
    displaySeg = segmentBetween(m.endpointA, m.endpointB);
  } else if (valueId === 'center' || valueId === 'min' || valueId === 'max') {
    if (!m.result.segments[valueId]) return;
    displaySeg = segmentForMode(m.result, valueId);
  }

  if (!displaySeg) {
    // Non-spatial row (angle, diameter, …): keep current graphics, only copy.
    return;
  }
  if (m.displayValueId === valueId) {
    setMeasurePanelPrimary(valueId);
    return;
  }

  disposeMesh(m.distLine);
  if (m.deltaGuide) { disposeMesh(m.deltaGuide); m.deltaGuide = null; }

  m.displayValueId = valueId;
  m.distLine = buildDistanceLine(displaySeg.a, displaySeg.b, deltaLineColor(valueId));
  m.mid.copy(displaySeg.mid);
  m.primaryDist = displaySeg.distance;

  if (showGuide) {
    m.deltaGuide = buildDeltaGuide(pickA, pickB);
  }

  setMeasurePanelPrimary(valueId);
  applyMeasurementVisibility(m);
  refreshMeasureList();
  requestRender();
}

function disposeMeasurementGraphics(m: Measurement): void {
  disposeMesh(m.distLine);
  if (m.deltaGuide) disposeMesh(m.deltaGuide);
  m.lines.forEach(disposeMesh);
  m.markers.forEach(disposeMesh);
  clearDimension(m);
  m.labelEl.remove();
  if (m.angleLabelEl) m.angleLabelEl.remove();
}

function applyDistanceMode(mode: DistanceMode): void {
  if (!currentMeasurement) return;
  if (!currentMeasurement.result.distanceModes.includes(mode)) return;
  if (currentMeasurement.mode === mode) return;
  const old = currentMeasurement;
  const [a, b] = old.picks;
  disposeMeasurementGraphics(old);
  createMeasurement(a, b, mode, old);
}

setMeasureModeChangeHandler(applyDistanceMode);
setMeasureValueSelectHandler(applyDisplayValue);

interface AngleArc {
  objects: THREE.Object3D[];
  labelPos: THREE.Vector3;
  angleRad: number;
}

// Dihedral angle arc — CAD-style: white filled sector, face-aligned arms,
// arc with arrowheads, label on the sector.
function buildAngleArc(pickA: Pick, pickB: Pick): AngleArc | null {
  const nA = pickA.normal.clone().normalize();
  const nB = pickB.normal.clone().normalize();

  const axis = new THREE.Vector3().crossVectors(nA, nB);
  if (axis.lengthSq() < 1e-12) return null;
  axis.normalize();

  const dirA = new THREE.Vector3().crossVectors(nA, axis).normalize();
  const dirB = new THREE.Vector3().crossVectors(axis, nB).normalize();
  if (dirA.lengthSq() < 1e-12 || dirB.lengthSq() < 1e-12) return null;

  const outward = nA.clone().add(nB);
  if (outward.lengthSq() > 1e-12) {
    outward.normalize();
    const bis = dirA.clone().add(dirB);
    if (bis.lengthSq() > 1e-12 && bis.normalize().dot(outward) > 0) {
      dirA.negate();
      dirB.negate();
    }
  }

  if (dirA.dot(dirB) < 0) dirB.negate();
  const ang = Math.acos(clamp(dirA.dot(dirB), -1, 1));
  if (!(ang > 1e-4)) return null;
  if (dirA.clone().cross(dirB).dot(axis) < 0) axis.negate();

  const modelRadius = getModelRadius();
  const center = pickA.point.clone().add(pickB.point).multiplyScalar(0.5);
  const wedgeDir = dirA.clone().add(dirB);
  if (wedgeDir.lengthSq() > 1e-12) {
    wedgeDir.normalize();
    center.addScaledVector(wedgeDir, Math.max(modelRadius * 0.008, markerRadius()));
  }

  const radius = Math.max(
    modelRadius * 0.12,
    pickA.point.distanceTo(pickB.point) * 0.4,
    markerRadius() * 12
  );

  const color = theme().accent;
  const objects: THREE.Object3D[] = [];
  const segs = Math.max(32, Math.round(ang * 64 / Math.PI));

  function dirAt(t: number): THREE.Vector3 {
    const q = new THREE.Quaternion().setFromAxisAngle(axis, ang * t);
    return dirA.clone().applyQuaternion(q);
  }

  // White filled sector
  const fillPositions: number[] = [center.x, center.y, center.z];
  const fillIndices: number[] = [];
  for (let i = 0; i <= segs; i++) {
    const p = center.clone().addScaledVector(dirAt(i / segs), radius);
    fillPositions.push(p.x, p.y, p.z);
    if (i > 0) fillIndices.push(0, i, i + 1);
  }
  const fillGeo = new THREE.BufferGeometry();
  fillGeo.setAttribute('position', new THREE.Float32BufferAttribute(fillPositions, 3));
  fillGeo.setIndex(fillIndices);
  const fillMesh = new THREE.Mesh(fillGeo, new THREE.MeshBasicMaterial({
    toneMapped: false,
    color: 0xffffff,
    transparent: true,
    opacity: 0.92,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide
  }));
  fillMesh.renderOrder = 995;
  scene.add(fillMesh);
  objects.push(fillMesh);

  function addLine(pts: THREE.Vector3[]): void {
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color, depthTest: false, toneMapped: false }));
    line.renderOrder = 997;
    scene.add(line);
    objects.push(line);
  }

  // Extension arms (slightly past the arc)
  addLine([center.clone(), center.clone().addScaledVector(dirA, radius * 1.08)]);
  addLine([center.clone(), center.clone().addScaledVector(dirB, radius * 1.08)]);

  // Arc
  const arcPts: THREE.Vector3[] = [];
  for (let i = 0; i <= segs; i++) arcPts.push(center.clone().addScaledVector(dirAt(i / segs), radius));
  addLine(arcPts);

  // Arrowheads at arc ends, pointing at the extension arms
  function addArrowHead(t: number): void {
    const radial = dirAt(t);
    const tip = center.clone().addScaledVector(radial, radius);
    const tang = new THREE.Vector3().crossVectors(axis, radial).normalize();
    // At start, point against +t; at end, point along +t
    if (t < 0.5) tang.negate();
    const len = radius * 0.1;
    const half = len * 0.5;
    const base = tip.clone().addScaledVector(tang, -len);
    const side = new THREE.Vector3().crossVectors(axis, tang).normalize().multiplyScalar(half);
    const b1 = base.clone().add(side);
    const b2 = base.clone().sub(side);
    const triGeo = new THREE.BufferGeometry();
    triGeo.setAttribute('position', new THREE.Float32BufferAttribute([
      tip.x, tip.y, tip.z, b1.x, b1.y, b1.z, b2.x, b2.y, b2.z
    ], 3));
    triGeo.setIndex([0, 1, 2]);
    const tri = new THREE.Mesh(triGeo, new THREE.MeshBasicMaterial({
      toneMapped: false,
      color, depthTest: false, depthWrite: false, side: THREE.DoubleSide
    }));
    tri.renderOrder = 998;
    scene.add(tri);
    objects.push(tri);
  }
  addArrowHead(0);
  addArrowHead(1);

  const labelPos = center.clone().addScaledVector(dirAt(0.5), radius * 0.62);
  return { objects, labelPos, angleRad: ang };
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

function updatePendingLabel(): void {
  if (!pendingLabel || !pendingPoint || pendingPoint.radius == null) return;
  placeScreenLabel(
    pendingLabel,
    pendingPoint.point,
    '⌀ ' + formatDistance(pendingPoint.radius * 2) + ' (R ' + formatDistance(pendingPoint.radius) + ')'
  );
}

function placeScreenLabel(el: HTMLElement | null, worldPos: THREE.Vector3 | null, text?: string): void {
  if (!el || !worldPos) return;
  const proj = worldPos.clone().project(camera);
  const x = (proj.x * 0.5 + 0.5) * window.innerWidth;
  const y = (-proj.y * 0.5 + 0.5) * window.innerHeight;
  const visible = proj.z < 1;
  el.style.display = visible ? 'block' : 'none';
  if (visible) {
    el.style.left = x + 'px';
    el.style.top = y + 'px';
    if (text != null) el.textContent = text;
  }
}

// ---------------------------------------------------------------------------
// Dimension
//
// A finished measurement is drawn the way a drawing dimensions it: the line
// runs between the two picked points, and each end carries a filled arrowhead
// pointing outwards. The arrowhead is a fixed size on screen (as on a sheet,
// where it does not grow with the dimension), so it is rebuilt from the camera
// every frame - which also keeps it facing the viewer as the model turns.
// ---------------------------------------------------------------------------

/** Arrowhead length in screen pixels. */
const ARROW_PX = 18;

interface DimensionArrow {
  mesh: THREE.Mesh;
  /** The measured point the arrow sits on. */
  tip: THREE.Vector3;
  /** Direction the arrow points, away from the other end. */
  along: THREE.Vector3;
}

const dimensionArrows = new WeakMap<Measurement, DimensionArrow[]>();

/**
 * How much world space one screen pixel covers at this point, so a size given
 * in pixels can be drawn in the scene.
 */
function worldPerPixel(at: THREE.Vector3): number {
  const height = Math.max(window.innerHeight, 1);
  if (camera instanceof THREE.OrthographicCamera) {
    return (camera.top - camera.bottom) / camera.zoom / height;
  }
  const dist = camera.position.distanceTo(at);
  return 2 * dist * Math.tan((camera.fov * Math.PI) / 360) / height;
}

function clearDimension(m: Measurement): void {
  const arrows = dimensionArrows.get(m);
  if (!arrows) return;
  for (const arrow of arrows) disposeMesh(arrow.mesh);
  dimensionArrows.delete(m);
}

function buildDimension(m: Measurement): void {
  clearDimension(m);
  const len = m.endpointA.distanceTo(m.endpointB);
  if (len < 1e-9) return;
  // The arrowhead marks the measured point, so the blob that used to sit there
  // would only cover it up.
  for (const marker of m.markers) marker.visible = false;

  const along = m.endpointB.clone().sub(m.endpointA).normalize();
  const arrows: DimensionArrow[] = [];
  for (const [tip, dir] of [[m.endpointA, along.clone().negate()], [m.endpointB, along]] as const) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(9), 3));
    geo.setIndex([0, 1, 2]);
    const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
      color: theme().teal, depthTest: false, depthWrite: false,
      side: THREE.DoubleSide, toneMapped: false
    }));
    mesh.renderOrder = 999;
    mesh.frustumCulled = false;
    scene.add(mesh);
    arrows.push({ mesh, tip: tip.clone(), along: dir.clone() });
  }
  dimensionArrows.set(m, arrows);
  updateDimension(m);
}

/** Re-aims and re-sizes the arrowheads for the camera as it is now. */
function updateDimension(m: Measurement): void {
  const arrows = dimensionArrows.get(m);
  if (!arrows) return;
  for (const arrow of arrows) {
    arrow.mesh.visible = measurementShown(m);
    if (!arrow.mesh.visible) continue;
    const size = Math.min(
      ARROW_PX * worldPerPixel(arrow.tip),
      m.endpointA.distanceTo(m.endpointB) * 0.3
    );
    // Flat against the screen: the arrow's width runs across the view.
    const toCamera = camera.position.clone().sub(arrow.tip);
    const side = new THREE.Vector3().crossVectors(arrow.along, toCamera);
    if (side.lengthSq() < 1e-12) side.set(0, 1, 0);
    side.normalize().multiplyScalar(size * 0.34);
    const base = arrow.tip.clone().addScaledVector(arrow.along, -size);
    const b1 = base.clone().add(side);
    const b2 = base.clone().sub(side);
    const pos = arrow.mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
    pos.setXYZ(0, arrow.tip.x, arrow.tip.y, arrow.tip.z);
    pos.setXYZ(1, b1.x, b1.y, b1.z);
    pos.setXYZ(2, b2.x, b2.y, b2.z);
    pos.needsUpdate = true;
    arrow.mesh.geometry.computeBoundingSphere();
  }
}

function updateMeasurementLabel(m: Measurement): void {
  m.labelEl.classList.toggle('inactive', m !== currentMeasurement);
  if (!measurementShown(m)) {
    m.labelEl.style.display = 'none';
    if (m.angleLabelEl) m.angleLabelEl.style.display = 'none';
    return;
  }
  let prefix = `#${m.id}  `;
  if (m.displayValueId === 'dx') prefix += 'ΔX ';
  else if (m.displayValueId === 'dy') prefix += 'ΔY ';
  else if (m.displayValueId === 'dz') prefix += 'ΔZ ';
  // Whether the two entities are parallel is a property of the measurement,
  // not of the model: it is in the panel, next to what was measured.
  updateDimension(m);
  placeScreenLabel(m.labelEl, m.mid, prefix + formatDistance(m.primaryDist));
  if (m.angleLabelEl && m.angleLabelPos) {
    placeScreenLabel(m.angleLabelEl, m.angleLabelPos, formatAngleDeg(m.angleDeg));
  }
}

// Labels are screen-space, so they only need repositioning on frames that are
// actually drawn (i.e. when the camera or the model changed).
onFrame(() => {
  for (const m of measurements) updateMeasurementLabel(m);
  if (pendingLabel) updatePendingLabel();
});

// ---------------------------------------------------------------------------
// Mode + clearing
// ---------------------------------------------------------------------------

function primaryText(m: Measurement): string {
  const v = m.result.values.find(x => x.id === m.displayValueId);
  if (!v) return formatDistance(m.primaryDist);
  return v.unit === 'deg' ? formatAngleDeg(v.value) : formatDistance(v.value);
}

function refreshMeasureList(): void {
  renderMeasureList(measurements.map(m => ({
    id: m.id,
    title: m.result.title,
    value: primaryText(m),
    visible: m.visible,
    active: m === currentMeasurement
  })));
}

/**
 * Measurements survive closing the tool, but they don't stay drawn over the
 * model: the two green faces and the floating label would hang there with no
 * obvious way to clear them. Each measurement keeps its own eye state in the
 * list, so this is a second, global gate on top of it.
 */
let graphicsHidden = false;

function measurementShown(m: Measurement): boolean {
  return m.visible && !graphicsHidden;
}

/** Pushes `m.visible` + `graphicsHidden` onto the three.js objects. */
function applyMeasurementVisibility(m: Measurement): void {
  const shown = measurementShown(m);
  m.distLine.visible = shown;
  if (m.deltaGuide) m.deltaGuide.visible = shown;
  m.lines.forEach(o => { o.visible = shown; });
  // Once the dimension arrows are up they mark the measured points, so the
  // marker blobs underneath stay down (see buildDimension).
  const markersShown = shown && !dimensionArrows.has(m);
  m.markers.forEach(o => { o.visible = markersShown; });
  updateMeasurementLabel(m);
}

function setMeasurementVisible(m: Measurement, visible: boolean): void {
  m.visible = visible;
  applyMeasurementVisibility(m);
}

/** Closing the tool parks every measurement; opening it brings them back. */
function setMeasurementGraphicsHidden(hidden: boolean): void {
  if (graphicsHidden === hidden) return;
  graphicsHidden = hidden;
  for (const m of measurements) applyMeasurementVisibility(m);
}

function showMeasurementDetails(m: Measurement | null): void {
  currentMeasurement = m;
  if (m) {
    renderMeasurePanel(m.result, m.mode, m.displayValueId);
    setMeasureStatus(`Measurement #${m.id} — click a value to copy; ΔX/Y/Z also shows in the model.`);
  } else {
    renderMeasurePanel(null, 'center', null);
    setMeasureStatus('Select first entity…');
  }
  for (const x of measurements) updateMeasurementLabel(x);
  refreshMeasureList();
  requestRender();
}

function removeMeasurement(m: Measurement): void {
  disposeMeasurementGraphics(m);
  measurements.splice(measurements.indexOf(m), 1);
  if (currentMeasurement === m) showMeasurementDetails(measurements[measurements.length - 1] ?? null);
  else refreshMeasureList();
  requestRender();
}

setMeasureListHandlers({
  select: id => {
    const m = measurements.find(x => x.id === id);
    if (m) showMeasurementDetails(m);
  },
  toggleVisible: id => {
    const m = measurements.find(x => x.id === id);
    if (!m) return;
    setMeasurementVisible(m, !m.visible);
    refreshMeasureList();
    requestRender();
  },
  remove: id => {
    const m = measurements.find(x => x.id === id);
    if (m) removeMeasurement(m);
  },
  clearAll: () => clearMeasurement()
});

onLengthUnitChange(() => {
  if (currentMeasurement) {
    renderMeasurePanel(currentMeasurement.result, currentMeasurement.mode, currentMeasurement.displayValueId);
  }
  refreshMeasureList();
  for (const m of measurements) updateMeasurementLabel(m);
  if (pendingLabel) updatePendingLabel();
});

/** Cancels a half-finished pick (first entity chosen) without touching the list. */
export function cancelPendingMeasure(): void {
  if (!pendingPoint) return;
  pendingPoint = null;
  clearPending();
  setMeasureStatus(currentMeasurement ? `Measurement #${currentMeasurement.id} — pick two entities for a new one.` : 'Select first entity…');
  requestRender();
}

/** Deletes every measurement (and a pending pick); numbering restarts at #1. */
export function clearMeasurement(): void {
  for (const m of measurements) disposeMeasurementGraphics(m);
  measurements.length = 0;
  currentMeasurement = null;
  nextMeasureId = 1;
  pendingPoint = null;
  clearPending();
  renderMeasurePanel(null, 'center', null);
  refreshMeasureList();
  if (measureMode) setMeasureStatus('Select first entity…');
  requestRender();
}

export function setMeasureMode(active: boolean): void {
  measureMode = active;
  dom.measureBtn.classList.toggle('primary', measureMode);
  dom.measureBtn.title = measureMode ? 'Measure: pick 2 entities (M)' : 'Measure (M)';
  dom.measureBtn.setAttribute('aria-pressed', measureMode ? 'true' : 'false');
  dom.canvas.style.cursor = measureMode ? 'crosshair' : 'grab';
  setMeasurePanelOpen(measureMode);
  setMeasurementGraphicsHidden(!measureMode);
  if (measureMode) {
    onMeasureActivated();
    if (!currentMeasurement && !pendingPoint) setMeasureStatus('Select first entity…');
  } else {
    clearHoverHighlight();
    if (pendingPoint) {
      pendingPoint = null;
      clearPending();
    }
    if (!currentMeasurement) {
      renderMeasurePanel(null, 'center', null);
      setMeasureStatus('Select first entity…');
    }
  }
  requestRender();
}

/** Toggle measure mode + panel (used by M key / toolbar). */
export function toggleMeasureMode(): void {
  setMeasureMode(!measureMode);
}

export { isMeasurePanelOpen, setMeasurePanelOpen };

// ---------------------------------------------------------------------------
// Hover preview: highlight the face under the cursor before it's clicked, same
// idea as Inventor/SolidWorks measure tools.
// ---------------------------------------------------------------------------

let hoverHighlight: THREE.Object3D | null = null;
let hoverKey: string | null = null;

export function clearHoverHighlight(): void {
  if (hoverHighlight) { disposeMesh(hoverHighlight); hoverHighlight = null; }
  hoverKey = null;
  dom.hoverTooltip.style.display = 'none';
}

export function updateHoverAt(clientX: number, clientY: number): void {
  const raw = pickFaceOnModel(clientX, clientY);
  if (!raw) { clearHoverHighlight(); return; }
  const rect = dom.canvas.getBoundingClientRect();
  const snap = snapTopology(raw.mesh, raw.brepFace, raw.triangleIndex, clientX, clientY, rect, renderer.clippingPlanes);

  let key: string;
  if (snap?.kind === 'vertex') {
    key = 'vertex:' + raw.mesh.uuid + ':'
      + snap.point.x.toFixed(3) + ',' + snap.point.y.toFixed(3) + ',' + snap.point.z.toFixed(3);
  } else if (snap?.kind === 'edge' && snap.edgeA && snap.edgeB) {
    key = 'edge:' + raw.mesh.uuid + ':'
      + snap.edgeA.x.toFixed(2) + ':' + snap.edgeB.x.toFixed(2)
      + ':' + snap.edgeA.y.toFixed(2) + ':' + snap.edgeB.y.toFixed(2);
  } else {
    key = 'face:' + raw.mesh.uuid + ':' + (raw.brepFace ? raw.brepFace.first : raw.triangleIndex);
  }

  dom.hoverTooltip.style.left = clientX + 'px';
  dom.hoverTooltip.style.top = clientY + 'px';
  dom.hoverTooltip.style.display = 'block';
  if (key === hoverKey) return;
  hoverKey = key;
  if (hoverHighlight) disposeMesh(hoverHighlight);

  if (snap?.kind === 'vertex') {
    const entity: Pick = { ...raw, kind: 'vertex', point: snap.point };
    hoverHighlight = buildEntityHighlight(entity, theme().hover, 0.4);
    dom.hoverTooltip.textContent = 'Vertex';
  } else if (snap?.kind === 'edge' && snap.edgeA && snap.edgeB) {
    const entity: Pick = {
      ...raw, kind: 'edge', point: snap.point, edgeA: snap.edgeA, edgeB: snap.edgeB
    };
    hoverHighlight = buildEntityHighlight(entity, theme().hover, 0.4);
    dom.hoverTooltip.textContent = 'Edge';
  } else {
    const analysis = enrichPick(raw);
    hoverHighlight = buildEntityHighlight(analysis, theme().hover, 0.4);
    dom.hoverTooltip.textContent = analysis.kind === 'cylinder' && analysis.radius != null
      ? 'Cylinder ⌀ ' + formatDistance(analysis.radius * 2) + ' (R ' + formatDistance(analysis.radius) + ')'
      : analysis.kind === 'plane' ? 'Plane' : 'Freeform';
  }
  requestRender();
}
